'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ExpandableContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode | string[];
  defaultExpanded?: boolean;
  keywords?: string[];
  maxLines?: number;
}

const ExpandableContent = React.forwardRef<HTMLDivElement, ExpandableContentProps>(
  ({ children, defaultExpanded = false, keywords = [], className, maxLines = 3, ...props }, ref) => {
    const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

    const highlightContent = React.useCallback((text: string) => {
      if (!keywords.length) return text;
      
      const pattern = new RegExp(`(${keywords.join('|')})`, 'gi');
      return text.split(pattern).map((part, i) => {
        if (keywords.some(keyword => part.toLowerCase() === keyword.toLowerCase())) {
          return <mark key={i} className="bg-accent rounded px-1">{part}</mark>;
        }
        return part;
      });
    }, [keywords]);

    const content = React.useMemo(() => {
      const textContent = Array.isArray(children) ? children.join('\n\n') : children;
      if (typeof textContent === 'string') {
        return highlightContent(textContent);
      }
      return textContent;
    }, [children, highlightContent]);

    // Check if content is likely a Markdown component by looking at className or props
    const isMarkdownContent = React.useMemo(() => {
      if (!React.isValidElement(children)) return false;
      
      // Check for className containing 'markdown'
      if (children.props && 
          children.props.className && 
          typeof children.props.className === 'string' && 
          children.props.className.includes('markdown')) {
        return true;
      }
      
      // Check for insideExpandable prop which is specific to our Markdown component
      if (children.props && 'insideExpandable' in children.props) {
        return true;
      }
      
      return false;
    }, [children]);

    return (
      <div ref={ref} {...props}>
        <div
          className="cursor-pointer relative"
          onClick={() => setIsExpanded(prev => !prev)}
          aria-expanded={isExpanded}
        >
          <div
            className={cn(
              'text-secondary pr-6',
              !isMarkdownContent && 'whitespace-pre-line',
              !isExpanded && {
                'line-clamp-1': maxLines === 1,
                'line-clamp-2': maxLines === 2,
                'line-clamp-3': maxLines === 3,
                'line-clamp-4': maxLines === 4,
                'line-clamp-5': maxLines === 5,
                'line-clamp-6': maxLines === 6,
                'overflow-hidden': maxLines > 6
              },
              className
            )}
            style={!isExpanded && maxLines > 6 ? {
              display: '-webkit-box',
              WebkitLineClamp: maxLines,
              WebkitBoxOrient: 'vertical',
            } : undefined}
          >
            {content}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setIsExpanded(prev => !prev); }}
            className="text-secondary hover:text-foreground transition-colors flex-shrink-0 absolute right-0 top-0"
            aria-label={isExpanded ? 'Show less' : 'Show more'}
          >
            {isExpanded ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    );
  }
);

ExpandableContent.displayName = 'ExpandableContent';

export { ExpandableContent };
export type { ExpandableContentProps };
